import { memo, useMemo } from 'react'
import { PriceFormat } from 'lightweight-charts'
import { ChartType } from '../../../proto/generated/chart'
import LiveText from '../../common/LiveText'
import {
    buildSeriesMetadataLiveSubscribe,
    getIndicatorCustomLabelSelector,
    getIndicatorValueSelector,
    getVolumeIndicatorColorSelector,
} from './seriesMetadataLiveDisplay'

type IndicatorMetadataLiveValueProps = {
    viewKey: string
    seriesGroupId: string
    seriesId: string
    priceFormat: PriceFormat['type']
    chartType: ChartType
    staticColor?: string
    useVolumeColor: boolean
    showValue: boolean
    withLabel: boolean
}

const valueStyle = { fontSize: '13px' } as const

const IndicatorMetadataLiveValue = ({
    viewKey,
    seriesGroupId,
    seriesId,
    priceFormat,
    chartType,
    staticColor,
    useVolumeColor,
    showValue,
    withLabel,
}: IndicatorMetadataLiveValueProps) => {
    const subscribe = useMemo(
        () => buildSeriesMetadataLiveSubscribe(viewKey, [seriesGroupId]),
        [viewKey, seriesGroupId]
    )

    const { text: valueSelector, color: colorSelector } = useMemo(
        () => getIndicatorValueSelector(viewKey, seriesGroupId, seriesId, priceFormat, chartType, staticColor),
        [viewKey, seriesGroupId, seriesId, priceFormat, chartType, staticColor]
    )

    const volumeColorSelector = useMemo(() => getVolumeIndicatorColorSelector(viewKey), [viewKey])
    const labelSelector = useMemo(
        () => getIndicatorCustomLabelSelector(viewKey, seriesGroupId, seriesId),
        [viewKey, seriesGroupId, seriesId]
    )

    const resolvedColorSelector = useVolumeColor ? volumeColorSelector : colorSelector

    if (!showValue) {
        return null
    }

    const valueNode = (
        <LiveText
            className="series-meta-value"
            style={valueStyle}
            subscribe={subscribe}
            selectText={valueSelector}
            selectColor={resolvedColorSelector}
        />
    )

    if (!withLabel) {
        return valueNode
    }

    return (
        <span className="series-meta-value-group">
            <LiveText
                className="series-meta-label"
                style={valueStyle}
                subscribe={subscribe}
                selectText={labelSelector}
            />
            {valueNode}
        </span>
    )
}

export default memo(IndicatorMetadataLiveValue)
